import * as d3 from "d3";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { AiOutlineDownload, AiOutlineInfo } from "react-icons/ai";
import { useDispatch, useSelector } from "react-redux";
import ReactTooltip from "react-tooltip";
import EvdaDepartmentFilterWrapper from "../../components/filter-widget/evdaDepartmentsFilterWrapper";
import departmentJSON from "../../components/map-widget/franceData";
import MapWidget from "../../components/map-widget/mapWidget";
import TableComponent, { DataTable } from "../../components/table-widget/tableComponent";
import DataDisplaySwitcher from "../../components/templates/components/dataDisplaySwitcher";
import SmallFilterAccessor from "../../components/templates/components/smallFilterAccessor";
import PageTemplate from "../../components/templates/pageTemplate";
import { DropDownItem } from "../../models/DropDownItem";
import { SelectedArea } from "../../models/SelectedArea";
import { SelectedGender } from "../../models/SelectedGender";
import { regionDictionary } from "../../models/zoneDectionary";
import { getDepartmentDisplayMode } from "../../redux/selectors/displayModeSelector";
import { getEvdaDepartmentsAgeFilterState, getEvdaDepartmentsDateFilterState, getEvdaDepartmentsGenderFilterState, getEvdaDepartmentsRegionFilterState } from "../../redux/selectors/filterSelector";
import { DisplayModeAction } from "../../redux/states/displayModeState";
import { FilterAction } from "../../redux/states/filterState";
import { DepartementMapPoint, MapPoint } from "../../service/MapPoint";
import { filterMapPointsData, getMaxDate } from "../../service/dataFilter";
import getData from "../../service/dataLoader";
import { useWindowWidthAndHeight } from "../../shared/hooks/customHooks";
import { getRightAge } from "../../shared/utils/getRightAge";
import { GetCurveTitleForEVDAPage } from "../../shared/utils/getRightTitle";
import { filterUnique } from "../../shared/utils/listUtils";
import { FrancemapCenter, getGraduationClass, providePathClassName } from "../../shared/utils/mapsUtils";
import { convertNumberToString } from "../../shared/utils/numbersUtils";

export type SelectedItem = {
  name: string;
  key: string;
  esperance_de_vie: string;
  rang: string;
};

const EvdaDepartmentsWidget = () => {
  // Hook capturant la largeur de la fenêtre
  const [width, height] = useWindowWidthAndHeight();
  // State du filtre sur l'âge
  const ageFilter = useSelector(getEvdaDepartmentsAgeFilterState);
  // State du filtre sur le genre
  const genderFilter = useSelector(getEvdaDepartmentsGenderFilterState);
  // State du filtre sur la date
  const dateFilter = useSelector(getEvdaDepartmentsDateFilterState);
  // State du filtre sur la région
  const regionsFilter = useSelector(getEvdaDepartmentsRegionFilterState);
  // Type de vue (tableau ou graphique)
  const displayMode = useSelector(getDepartmentDisplayMode);
  const [tooltipId, setTooltipId] = useState<string>(displayMode.isChartMode ? "tableDisplayMode" : "chartDisplayMode");
  // Jeu complet de données
  const [EvdaDepartmentsWidgetData, setEvdaDepartmentsWidgetData] = useState<MapPoint[]>([]);
  // Jeu de données à afficher
  const [DataToDisplay, setDataToDisplay] = useState<MapPoint[]>([]);
  // Clé dans les fichiers de localisation du titre de la carte
  const [mapTitleKey, setMapTitleKey] = useState<string | TemplateStringsArray | (string | TemplateStringsArray)[]>("evda_birth_curve_title");
  // Liste des âges disponibles dans la dropdown des âges
  const [differentAge, setDifferentAge] = useState<DropDownItem[]>([]);
  // Date maximale du jeu de données
  const [maxDate, setMaxDate] = useState<number>(0);
  const [orientation, setOrientation] = useState<"landscape" | "portrait">("portrait");

  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    // Sauvegarde de l'onglet chargé
    dispatch(FilterAction.SetArea(SelectedArea.Departments));

    // Chargement initial du jeu de données complet depuis le CSV
    getData(`fr/outil-interactif/6794598/data/esperance_vie_departement.csv`).then((parsed) => {
      // Mapping de la propriété departement vers node
      const parsedData: MapPoint[] = parsed.data.map((mapPoint: DepartementMapPoint) => ({
        ...mapPoint,
        node: mapPoint.departement,
      }));

      // Set du jeu de données complet dans l'état du composant
      setEvdaDepartmentsWidgetData(parsedData);
    });
  }, []);

  // Use effect réagissant au changement de données global pour affecter la date maximale
  useEffect(() => {
    if (EvdaDepartmentsWidgetData.length > 0) {
      // Mise à jour de la date maximale fournie par le fichier
      setMaxDate(getMaxDate(EvdaDepartmentsWidgetData));

      // Mise à jour de la liste des âges
      setDifferentAge(computeAgeList(EvdaDepartmentsWidgetData));
    }
  }, [EvdaDepartmentsWidgetData]);

  useEffect(() => {
    if (width > height) {
      setOrientation("landscape");
    } else {
      setOrientation("portrait");
    }
  }, [width, height]);

  // Use effect réagissant au changement de date maximale pour charger le premier jeu de données sur la carte
  useEffect(() => {
    if (EvdaDepartmentsWidgetData.length > 0 && maxDate > 0) {
      // Set par défaut de la date du filtre sur la date maximale
      if (dateFilter.selectedyear === 0) {
        // Cette action déclenche le useEffect qui écoute les states des filtres
        // filtrant initialement le jeu de données et affichant ainsi la carte
        dispatch(FilterAction.setEvdaDepartmentsSelectedYear(maxDate));
      }

      // Chargement initial des données sur la carte
      computeData();
    }
  }, [maxDate]);

  /**
   * Génère la liste des âges pour la dropdown
   * @param mapPoints le jeu de données à partir duquel extraire les âges disponibles
   * @returns la liste de DropDownItem à donner au filtre
   */
  function computeAgeList(mapPoints: MapPoint[]): DropDownItem[] {
    return (
      mapPoints
        // Mapping sur les âges du jeu
        .map((mapPoint: MapPoint) => parseInt(mapPoint.age))
        // Exclusions des âges 0 et 65 qui ont des boutons dédiés, et des NaN issus d'éventuelles lignes vides ou invalides
        .filter((age: number) => !Number.isNaN(age) && age !== 0 && age !== 65)
        // Suppression des doublons
        .filter(filterUnique)
        // Mapping de sortie vers l'objet DropDownItem
        .map((age: number) => ({
          Element: {
            key: age.toString(),
            name: t("age_unit_interval", {
              postProcess: "interval",
              count: age,
            }),
          },
          selected: false,
        }))
    );
  }

  function computeData(): void {
    const result = filterMapPointsData(EvdaDepartmentsWidgetData, genderFilter.selectedGender, getRightAge(ageFilter.seletedAge, ageFilter.customSelectedAge), dateFilter.selectedyear);

    setDataToDisplay(result);
    setMapTitleKey(GetCurveTitleForEVDAPage(ageFilter.seletedAge, ageFilter.customSelectedAge, SelectedArea.Departments, genderFilter.selectedGender));
  }

  useEffect(() => {
    setTooltipId(displayMode.isChartMode ? "chartDisplayMode" : "tableDisplayMode");
  }, [displayMode.isChartMode]);

  // use effect réagissant aux filtres pour mettre à jour le jeu de données
  useEffect(() => {
    computeData();
  }, [ageFilter, genderFilter.selectedGender, dateFilter.selectedyear]);

  // Use effect réagissant au changement de lange pour mettre à jour la liste des âges
  useEffect(() => {
    setDifferentAge(computeAgeList(EvdaDepartmentsWidgetData));
  }, [t]);

  const provideFilterWidget = () => {
    return <EvdaDepartmentFilterWrapper maxDate={maxDate} differentAges={differentAge} data={DataToDisplay} />;
  };

  const provideSelectedRegions = (selected: DropDownItem[]) => {
    let countriesTosend: SelectedItem[] = [];
    if (selected.length > 0) {
      selected.forEach(function (region) {
        const found = DataToDisplay.filter((element) => element.node === region.Element.key);
        let evda = "nd";
        let rang = -1;
        if (found && found.length > 0) {
          evda = DataToDisplay.filter((element) => element.node === region.Element.key)[0].esperance_de_vie;
          rang = DataToDisplay.filter((element) => element.node === region.Element.key)[0].rang;
        }
        countriesTosend.push({
          key: region.Element.key,
          name: region.Element.name,
          esperance_de_vie: evda,
          rang: rang?.toString(),
        });
      });
    }
    return countriesTosend;
  };

  const provideTableTitles = () => {
    let titles: string[] = [];
    titles.push(t("department"));
    if (genderFilter.selectedGender === SelectedGender.male) {
      titles.push(t("men"));
    } else if (genderFilter.selectedGender === SelectedGender.male_women_diff) {
      titles.push(t("gap"));
    } else {
      titles.push(t("women"));
    }
    titles.push(t("department_rank"));
    return titles;
  };

  const provideDataToTableDisplay = () => {
    let dataTable: DataTable[] = [];
    if (regionsFilter.selectedRegions.length === 0) {
      DataToDisplay.forEach(function (data) {
        const departmentName = regionDictionary.filter((element) => element.key === data.node)[0].name;
        const element: DataTable = {
          key: "key" + data.node,
          columns: [
            { data: departmentName + " (" + data.node + ")", component: "th" },
            {
              data: data.esperance_de_vie !== "nd" && data.esperance_de_vie !== "NaN" ? convertNumberToString(parseFloat(data.esperance_de_vie), i18n, 1, 1) : t("value_not_available"),
            },
            {
              data: data.rang && data.rang ? data.rang.toString() : t("value_not_available"),
            },
          ],
        };
        dataTable.push(element);
      });
    } else {
      const filterResult = DataToDisplay.filter((e) => regionsFilter.selectedRegions.filter((region) => region.Element.key === e.node).length > 0);
      filterResult.forEach(function (data) {
        const departmentName = regionDictionary.filter((element) => element.key === data.node)[0].name;
        const element: DataTable = {
          key: data.node,
          columns: [
            { data: departmentName + " (" + data.node + ")", component: "th" },
            {
              data: data.esperance_de_vie !== "nd" && data.esperance_de_vie !== "NaN" ? convertNumberToString(parseFloat(data.esperance_de_vie), i18n, 1, 1) : t("value_not_available"),
            },
            {
              data: data.rang && data.rang > 0 ? data.rang.toString() : t("value_not_available"),
            },
          ],
        };
        dataTable.push(element);
      });
    }
    return dataTable;
  };

  function projectionGetter(width: number, height: number): d3.GeoConicProjection {
    return (
      d3
        .geoConicConformal()
        // Coordonnées géographiques du centre de la France, prenant en compte les DOM,
        // placés au sud des Pyrénées
        .center(FrancemapCenter(width))
        // Facteur de zoom empirique pour projeter la carte avec un niveau de zoom humainement acceptable
        .scale(Math.min(width * 4, height * 5.5))
        .translate([width / 2, height / 2])
    );
  }

  function drawNodes(path: d3.GeoPath<any, any>, deps: d3.Selection<SVGGElement, unknown, HTMLElement, any>, dataToDisplay: MapPoint[], selectedGender: SelectedGender): void {
    deps
      .selectAll("path")
      .data(departmentJSON.features)
      .enter()
      .append("path")
      .attr("d", path)
      .attr("id", (d) => "d" + d.properties.CODE)
      .attr("class", "region q0-5")
      .attr("class", (d) => {
        const e = dataToDisplay.find((data) => data.node === d.properties.CODE.toString());
        return e !== undefined && getGraduationClass(dataToDisplay, parseFloat(e.esperance_de_vie)) !== undefined ? providePathClassName(selectedGender) + getGraduationClass(dataToDisplay, parseFloat(e.esperance_de_vie)) + "-5" : "region q0-5";
      });
  }

  const onSetDisplayModeEvent = () => {
    dispatch(DisplayModeAction.changeDepartmentDisplayMode());
  };

  const onRegionCLickInMap = (item: DropDownItem) => {
    dispatch(FilterAction.setEvdaDepartmentsRegionSelection(item));
  };

  const provideMap = () => {
    const style1 = { color: "#ffffff" };
    const style2 = { color: "#0C3A5A" };

    return (
      <div className="display-data">
        <Helmet>
          <title>{t("evda-department-title")}</title>
        </Helmet>
        <div className="display-data-header">
          <h3>
            {t("" + mapTitleKey, {
              year: dateFilter.selectedyear,
              age: ageFilter.customSelectedAge,
              unit: ageFilter.customSelectedAge === 1 ? "an" : "ans",
            })}
          </h3>
          <div className="graph-button-container">
            {width < 1150 ? <SmallFilterAccessor></SmallFilterAccessor> : undefined}

            <DataDisplaySwitcher tooltipId="displayMode" isChartMode={displayMode.isChartMode} setModeHandler={onSetDisplayModeEvent}></DataDisplaySwitcher>

            <span aria-label="Informations supplémentaires sur les données d'espérance de vie par département" aria-details="infoTip" data-tip data-for="infoTip" className="info-round-button">
              <AiOutlineInfo style={style2}></AiOutlineInfo>
            </span>

            <a href={`fr/outil-interactif/6794598/data/downloads/Esperance_Vie_DEPARTEMENT.xlsx`} aria-label="Téléchargement des données complètes d'espérance de vie par département au format .xlsx" data-tip data-for="downloadTip" className="download-round-button">
              <AiOutlineDownload style={style1}></AiOutlineDownload>
            </a>

            <ReactTooltip id="infoTip" place="bottom" effect="solid" multiline={true} html={true}>
              {t("tooltip-info-evda-departments")}
            </ReactTooltip>

            <ReactTooltip id="downloadTip" place="bottom" effect="solid" multiline={true} html={true}>
              {t("tooltip-download-evda-department")}
            </ReactTooltip>

            <ReactTooltip id="displayMode" place="bottom" effect="solid" multiline={true} html={true}>
              {
                // Maintenir cet variable permet de formcer le rechargement de la carte
                // Lors du changement de display mode (plantage sinon)
                tooltipId === "chartDisplayMode" ? t("tooltip-change-display-table-mode") : t("tooltip-change-display-chart-mode")
              }
            </ReactTooltip>
          </div>
        </div>

        {displayMode.isChartMode ? (
          <MapWidget
            orientation={orientation}
            map={departmentJSON}
            nodeIdGetter={(feature) => feature.properties.CODE.toString()}
            nodeNameGetter={(feature) => feature.properties.name}
            projectionGetter={projectionGetter}
            drawNodes={drawNodes}
            dictionary={regionDictionary}
            itemClickHandler={onRegionCLickInMap}
            selectedGender={genderFilter.selectedGender}
            data={DataToDisplay}
            selectedItems={provideSelectedRegions(regionsFilter.selectedRegions)}
          />
        ) : undefined}
        {!displayMode.isChartMode ? (
          <TableComponent
            caption={t("" + mapTitleKey, {
              age: ageFilter.customSelectedAge,
              unit: ageFilter.customSelectedAge === 1 ? "an" : "ans",
            })}
            unit={t("year_unit_plural", { postProcess: "plural" })}
            data={provideDataToTableDisplay()}
            titles={provideTableTitles()}
          ></TableComponent>
        ) : undefined}
      </div>
    );
  };

  return <PageTemplate pageName="Department" dataSource={t("evda_department_data_source")} graphContent={provideMap()} filterContent={provideFilterWidget()} />;
};

export default EvdaDepartmentsWidget;
